import React, { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import EsitoBadge from "../../components/EsitoBadge";

const formatDateIT = (iso) => {
  if (!iso) return "—";
  const s = String(iso).slice(0, 10);
  const [y, m, d] = s.split("-");
  return y && m && d ? `${d}/${m}/${y}` : iso;
};

const photoUrl = (ph) =>
  ph?.external_url || (ph?.path ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${ph.path}` : null);

const Field = ({ label, value, testid }) => (
  <div>
    <div className="label-uppercase text-[#5C5657]">{label}</div>
    <div className="text-sm text-[#3A3D42] mt-1" data-testid={testid}>{value ?? "—"}</div>
  </div>
);

function StatoBadge({ p }) {
  if (p.stato_pubblicazione === "venduto") {
    return (
      <span className="badge-stamp badge-non_conforme" data-testid="stato-venduto">
        Venduto {p.data_vendita ? `· ${formatDateIT(p.data_vendita)}` : ""}
      </span>
    );
  }
  if (p.stato_pubblicazione === "in_vendita") {
    return <span className="badge-stamp badge-pronto" data-testid="stato-in-vendita">Disponibile</span>;
  }
  return <span className="text-xs text-[#5C5657]">—</span>;
}

function DetailView({ property, onClose }) {
  const [full, setFull] = useState(property);
  const [heroIdx, setHeroIdx] = useState(0);

  useEffect(() => {
    // Ricarica per avere seller e foto complete
    api.get(`/agency/properties/${property.id}`).then((r) => setFull(r.data)).catch(() => {});
  }, [property.id]);

  const p = full || property;
  const photos = p.photos || [];
  const seller = p.seller || null;

  return (
    <div
      className="fixed inset-0 bg-[#3A3D42]/40 flex items-start justify-center overflow-auto z-50 p-6"
      data-testid="ag-detail-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white border border-[#E6E4E0] w-full max-w-5xl my-8">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-[#E6E4E0] px-8 py-5 flex justify-between items-start z-10">
          <div>
            <div className="label-uppercase text-[#7D2E3F]">
              {p.tipologia} · {p.comune}{p.provincia ? ` (${p.provincia})` : ""}
            </div>
            <h2 className="font-serif text-3xl mt-1" data-testid="detail-titolo">{p.titolo}</h2>
            <div className="mt-2 flex flex-wrap items-center gap-3">
              {p.fascicolo_code && (
                <span className="font-mono text-xs text-[#7D2E3F] border border-[#E6E4E0] px-2 py-1" data-testid="detail-fascicolo">
                  {p.fascicolo_code}
                </span>
              )}
              <EsitoBadge esito={p.esito} />
              <StatoBadge p={p} />
            </div>
          </div>
          <button onClick={onClose} data-testid="detail-close" className="label-uppercase text-xs text-[#5C5657]">Chiudi ×</button>
        </div>

        <div className="p-8 space-y-8">
          {/* Banner sola consultazione */}
          <div className="border border-[#F2EBEB] bg-[#FDFDFC] p-4 text-xs text-[#5C5657]" data-testid="readonly-banner">
            Scheda in <strong>sola consultazione</strong>. L&apos;integrità del Protocollo, dei dati anagrafici e del Fascicolo
            è garantita da <strong>RX CASA</strong>. Per aggiornamenti contatta il tuo referente di rete.
          </div>

          {/* Foto */}
          {photos.length > 0 && (
            <section data-testid="detail-photos">
              <div className="label-uppercase mb-3">Fotografia</div>
              <div className="aspect-[16/9] bg-[#F4F3F1] border border-[#E6E4E0] overflow-hidden">
                {photoUrl(photos[heroIdx]) && (
                  <img src={photoUrl(photos[heroIdx])} alt="" className="w-full h-full object-cover" />
                )}
              </div>
              {photos.length > 1 && (
                <div className="grid grid-cols-6 gap-2 mt-3">
                  {photos.map((ph, idx) => (
                    <button
                      key={ph.id || idx}
                      onClick={() => setHeroIdx(idx)}
                      data-testid={`detail-thumb-${idx}`}
                      className={`aspect-square bg-[#F4F3F1] border overflow-hidden ${idx === heroIdx ? "border-[#7D2E3F]" : "border-[#E6E4E0]"}`}
                    >
                      {photoUrl(ph) && <img src={photoUrl(ph)} alt="" className="w-full h-full object-cover" />}
                    </button>
                  ))}
                </div>
              )}
            </section>
          )}

          {/* Dati anagrafici */}
          <section>
            <div className="label-uppercase mb-3">Dati anagrafici</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6 border border-[#E6E4E0] bg-[#FDFDFC] p-6">
              <Field label="Tipologia" value={p.tipologia} testid="anag-tipologia" />
              <Field label="Superficie" value={p.superficie ? `${p.superficie} m²` : null} testid="anag-superficie" />
              <Field label="Locali" value={p.locali || null} testid="anag-locali" />
              <Field label="Camere" value={p.camere || null} testid="anag-camere" />
              <Field label="Bagni" value={p.bagni || null} testid="anag-bagni" />
              <Field label="Piano" value={p.piano || null} testid="anag-piano" />
              <Field label="Zona" value={p.zona || null} testid="anag-zona" />
              <div className="col-span-2 md:col-span-3">
                <Field
                  label="Indirizzo"
                  value={[p.indirizzo, p.comune && `${p.comune}${p.provincia ? ` (${p.provincia})` : ""}`, p.cap].filter(Boolean).join(" · ") || null}
                  testid="anag-indirizzo"
                />
              </div>
              <Field label="Prezzo" value={p.prezzo ? `€ ${Number(p.prezzo).toLocaleString("it-IT")}` : null} testid="anag-prezzo" />
              <Field label="Ingresso Galleria" value={formatDateIT(p.data_ingresso_galleria)} testid="anag-ingresso" />
            </div>
          </section>

          {/* Descrizione */}
          {p.descrizione && (
            <section>
              <div className="label-uppercase mb-3">Descrizione commerciale</div>
              <div className="border border-[#E6E4E0] bg-white p-6 text-sm text-[#3A3D42] whitespace-pre-line" data-testid="detail-descrizione">
                {p.descrizione}
              </div>
            </section>
          )}

          {/* Venditore collegato (solo dati operativi) */}
          {seller ? (
            <section data-testid="detail-seller">
              <div className="label-uppercase mb-3">Venditore collegato</div>
              <div className="border border-[#E6E4E0] bg-white p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <Field
                    label="Nominativo"
                    value={[seller.nome, seller.cognome].filter(Boolean).join(" ") || null}
                    testid="seller-nome"
                  />
                  <Field label="Telefono" value={seller.telefono || null} testid="seller-phone" />
                  <Field label="Email" value={seller.email || null} testid="seller-email" />
                </div>
                <div className="mt-5 pt-4 border-t border-[#F2EBEB] flex items-center gap-3">
                  <div className="label-uppercase text-[#5C5657]">Consenso pubblicazione:</div>
                  {seller.consenso_pubblicazione ? (
                    <span className="text-xs label-uppercase text-[#2E7D3F] border border-[#2E7D3F] px-2 py-1" data-testid="seller-consenso-ok">
                      OK{seller.consenso_pubblicazione_at ? ` · ${formatDateIT(seller.consenso_pubblicazione_at)}` : ""}
                    </span>
                  ) : (
                    <span className="text-xs label-uppercase text-[#B3261E] border border-[#B3261E] px-2 py-1" data-testid="seller-consenso-ko">
                      Mancante
                    </span>
                  )}
                </div>
                <div className="mt-4 text-xs text-[#5C5657]">
                  I dati sensibili del venditore (codice fiscale, documento d&apos;identità, residenza, data di nascita)
                  sono trattati esclusivamente da RX CASA nel rispetto del GDPR e non sono accessibili in questa area.
                </div>
              </div>
            </section>
          ) : (
            <section>
              <div className="label-uppercase mb-3">Venditore collegato</div>
              <div className="border border-[#E6E4E0] bg-[#FDFDFC] p-6 text-sm text-[#5C5657]" data-testid="detail-seller-empty">
                Nessun venditore ancora associato a questo immobile in RX CASA.
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AgenziaImmobili() {
  const [items, setItems] = useState([]);
  const [viewing, setViewing] = useState(null);
  const [agencyAgents, setAgencyAgents] = useState([]);

  const [q, setQ] = useState("");
  const [fComune, setFComune] = useState("");
  const [fEsito, setFEsito] = useState("");
  const [fAgente, setFAgente] = useState("");

  const load = () => api.get("/agency/properties").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/agency/profile").then((r) => setAgencyAgents(r.data?.agents || [])).catch(() => {});
  }, []);

  const comuneOptions = useMemo(
    () => Array.from(new Set(items.map((p) => p.comune).filter(Boolean))).sort(),
    [items]
  );
  // Opzioni Agente: agenti profilo agenzia + eventuali agente_nome storici sugli immobili (dedup)
  const agenteOptions = useMemo(() => {
    const set = new Set();
    (agencyAgents || []).forEach((a) => { if (a?.name) set.add(a.name); });
    items.forEach((p) => { if (p.agente_nome) set.add(p.agente_nome); });
    return Array.from(set).sort((a, b) => a.localeCompare(b, "it"));
  }, [agencyAgents, items]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return items.filter((p) => {
      if (needle) {
        const hay = [p.fascicolo_code, p.titolo, p.comune, p.indirizzo].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      if (fComune && p.comune !== fComune) return false;
      if (fEsito && p.esito !== fEsito) return false;
      if (fAgente && p.agente_nome !== fAgente) return false;
      return true;
    });
  }, [items, q, fComune, fEsito, fAgente]);
  const anyFilterActive = !!(q || fComune || fEsito || fAgente);
  const resetFilters = () => { setQ(""); setFComune(""); setFEsito(""); setFAgente(""); };

  return (
    <div data-testid="agenzia-immobili">
      <div className="label-uppercase">La mia agenzia</div>
      <h1 className="font-serif text-4xl mt-2 mb-3">I miei immobili</h1>
      <p className="text-sm text-[#5C5657] mb-6 max-w-2xl" data-testid="readonly-intro">
        Scheda in sola consultazione. L&apos;integrità del Protocollo e dei dati è garantita da RX CASA · garante del Protocollo.
      </p>

      {/* Filtri */}
      <div className="mb-4 border border-[#E6E4E0] bg-white p-3 flex flex-wrap items-center gap-2" data-testid="ag-filters-bar">
        <input
          data-testid="ag-filter-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Cerca codice Fascicolo, titolo, comune…"
          className="admin-input flex-1 min-w-[240px] max-w-md"
        />
        <select data-testid="ag-filter-comune" value={fComune} onChange={(e) => setFComune(e.target.value)} className="admin-input w-44">
          <option value="">Comune (tutti)</option>
          {comuneOptions.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
        <select data-testid="ag-filter-esito" value={fEsito} onChange={(e) => setFEsito(e.target.value)} className="admin-input w-56">
          <option value="">Esito (tutti)</option>
          <option value="pronto">Pronto alla Vendita</option>
          <option value="criticita">Con criticità dichiarate</option>
          <option value="non_conforme">Non conforme</option>
        </select>
        <select data-testid="ag-filter-agente" value={fAgente} onChange={(e) => setFAgente(e.target.value)} className="admin-input w-52">
          <option value="">Agente (tutti)</option>
          {agenteOptions.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
        <button
          data-testid="ag-filter-reset"
          onClick={resetFilters}
          disabled={!anyFilterActive}
          className={`text-xs label-uppercase px-3 py-2 border ${anyFilterActive ? "border-[#7D2E3F] text-[#7D2E3F] hover:bg-[#FDFDFC]" : "border-[#E6E4E0] text-[#B8B4AD] cursor-not-allowed"}`}
        >
          Reset filtri
        </button>
        <div className="ml-auto text-xs text-[#5C5657]" data-testid="ag-filter-count">
          {filtered.length} / {items.length}
        </div>
      </div>

      <table className="admin-table w-full bg-white border border-[#E6E4E0]">
        <thead>
          <tr>
            <th>Titolo</th>
            <th>Fascicolo</th>
            <th>Comune</th>
            <th>Esito</th>
            <th>Stato</th>
            <th>Prezzo</th>
            <th>Ingresso</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((p) => (
            <tr key={p.id} data-testid={`ag-prop-${p.id}`}>
              <td>
                <div className="font-medium">{p.titolo}</div>
                <div className="text-xs text-[#5C5657]">{p.tipologia}{p.superficie ? ` · ${p.superficie}m²` : ""}</div>
              </td>
              <td className="font-mono text-xs whitespace-nowrap text-[#7D2E3F]" data-testid={`ag-prop-code-${p.id}`}>
                {p.fascicolo_code || "—"}
              </td>
              <td>{p.comune}</td>
              <td><EsitoBadge esito={p.esito} /></td>
              <td className="text-xs whitespace-nowrap"><StatoBadge p={p} /></td>
              <td className="whitespace-nowrap">{p.prezzo ? `€ ${Number(p.prezzo).toLocaleString("it-IT")}` : "—"}</td>
              <td className="text-xs whitespace-nowrap">{formatDateIT(p.data_ingresso_galleria)}</td>
              <td className="text-right">
                <button
                  data-testid={`ag-view-${p.id}`}
                  onClick={() => setViewing(p)}
                  className="text-xs label-uppercase text-[#7D2E3F]"
                >
                  Dettagli
                </button>
              </td>
            </tr>
          ))}
          {filtered.length === 0 && (
            <tr>
              <td colSpan="8" className="text-center py-8 text-[#5C5657]">
                {anyFilterActive ? "Nessun immobile corrisponde ai filtri" : "Nessun immobile associato alla tua agenzia"}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {viewing && <DetailView property={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}
